import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowUpRight,
  Bot,
  Check,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  CircleSlash,
  CircleX,
  Hash,
  Inbox,
  MessageSquareText,
  Send,
  Settings2,
  TriangleAlert,
  UserCheck,
  Users,
  Zap,
} from 'lucide-react'

import { hasPermission, type AdminWithdrawal, type AutoWithdrawalsStatus, type BroadcastErrorCode, type WithdrawalAutoSkipReason, type WithdrawalStatus } from '@app/shared'

import { PageHeader } from '../components/layout'
import { approveWithdrawal, failWithdrawal, getAutoWithdrawalsStatus, getCurrentUser, getOverview, getWithdrawals, rejectWithdrawal, sendWithdrawal } from '../api/admin'
import {
  Avatar,
  Badge,
  Button,
  Callout,
  CountUp,
  EmptyState,
  Field,
  IdChip,
  Input,
  Modal,
  Pager,
  Panel,
  Progress,
  Skeleton,
  StatusPill,
  Table,
  TableSkeleton,
  Tabs,
  Textarea,
  Tooltip,
  cx,
  describeError,
  formatNumber,
  money,
  short,
  statusLabel,
  tableClass,
  useToast,
  useTotp,
  when,
  type Tone,
} from '../components/ui/Ui'
import styles from './Withdrawals.module.css'

type Tab = 'pending' | 'approved' | 'broadcasting' | 'sent' | 'rejected' | 'failed' | 'all'

const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'pending', label: 'На проверке' },
  { value: 'approved', label: 'К отправке' },
  { value: 'broadcasting', label: 'В отправке' },
  { value: 'sent', label: 'Отправлены' },
  { value: 'rejected', label: 'Отклонены' },
  { value: 'failed', label: 'Не удались' },
  { value: 'all', label: 'Все' },
]

/** What an empty tab says: the two queues read as «done», the archives as «nothing yet». */
const EMPTY_RU: Record<Tab, { title: string; description: string; done: boolean }> = {
  pending: { title: 'Очередь проверки пуста', description: 'Новые заявки появятся здесь сами: список обновляется каждые 15 секунд.', done: true },
  approved: { title: 'Отправлять нечего', description: 'Одобренные заявки, которые ждут отправки или повтора после сбоя, появятся здесь.', done: true },
  broadcasting: { title: 'В сети ничего не ждёт', description: 'Здесь видны заявки, которые горячий кошелёк сейчас отправляет.', done: true },
  sent: { title: 'Отправленных заявок нет', description: 'Здесь будут заявки, чьи транзакции ушли в сеть.', done: false },
  rejected: { title: 'Отклонённых заявок нет', description: 'Здесь будут заявки, отклонённые оператором, с причиной.', done: false },
  failed: { title: 'Неудавшихся заявок нет', description: 'Здесь будут заявки, отмеченные как неудавшиеся; средства по ним вернулись пользователям.', done: false },
  all: { title: 'Заявок нет', description: 'Здесь появится каждая заявка на вывод, в любом статусе.', done: false },
}

const TABLE_HEADERS = ['Пользователь · создана', 'Актив · сеть', 'Сумма', 'Адрес · хеш', 'Статус', ''] as const

/** «Заявка … одобрена»: the toast speaks of a request (feminine), the pills of a status. */
const REQUEST_RESULT_RU: Partial<Record<WithdrawalStatus, string>> = {
  approved: 'одобрена',
  rejected: 'отклонена, средства вернулись пользователю',
  sent: 'отправлена',
  confirmed: 'подтверждена',
  failed: 'отмечена как неудавшаяся, средства вернулись пользователю',
  broadcasting: 'передана в сеть',
}

/** `record` settles a stuck broadcast with the hash found on the explorer. */
type Decision = { kind: 'reject' | 'fail' | 'send' | 'record'; row: AdminWithdrawal }

/** The dialog's icon and its tile's tone, per decision. */
const DECISION_LOOK: Record<Decision['kind'], { icon: ReactNode; tone: Tone }> = {
  send: { icon: <Send />, tone: 'accent' },
  record: { icon: <Hash />, tone: 'accent' },
  reject: { icon: <CircleX />, tone: 'down' },
  fail: { icon: <CircleSlash />, tone: 'down' },
}

/** Why the automation left a request for a person, as the queue says it. */
const AUTO_REASON_RU: Record<WithdrawalAutoSkipReason, string> = {
  kyc: 'верификация не одобрена',
  account: 'аккаунт не активен или служебный',
  account_age: 'аккаунт слишком новый',
  security_change: 'недавно менялись пароль или 2FA',
  risk_flag: 'флаг риска: пересмотр депозита или партнёрская проверка',
  address_book: 'адрес не подтверждён в адресной книге',
  new_address: 'первый вывод на этот адрес',
  amount: 'сумма выше лимита автовывода',
  four_eyes: 'нужны два оператора',
  no_price: 'нет цены в USDT',
  no_hot_wallet: 'нет горячего кошелька для сети',
  route_disabled: 'маршрут вывода выключен',
  operator: 'оператор уже начал проверку',
  user_daily_limit: 'лимит автовывода на пользователя за сутки исчерпан',
  daily_cap: 'дневной бюджет автовывода исчерпан',
}

/** What the hot wallet ran into, in the panel's language; the node's own words stay in the tooltip. */
const BROADCAST_ERROR_RU: Record<BroadcastErrorCode, string> = {
  not_configured: 'горячий кошелёк сети не настроен',
  wallet_busy: 'горячий кошелёк был занят другой отправкой',
  send_lock_unavailable: 'лок отправки недоступен: Redis не ответил, ничего не подписано',
  invalid_destination: 'адрес получателя недействителен для сети',
  destination_is_hot_wallet: 'адрес получателя — сам горячий кошелёк',
  destination_is_token_account: 'адрес получателя — токен-аккаунт, а не кошелёк',
  destination_is_program: 'адрес получателя — программа, а не кошелёк',
  amount_too_small: 'сумма округляется до нуля в единицах сети',
  registry_mismatch: 'реестр не совпадает с сетью: контракт, mint или знаки после запятой',
  node_unreachable: 'узел сети не ответил',
  rate_limited: 'узел сети ограничил частоту запросов',
  insufficient_funds: 'на горячем кошельке не хватает средств или газа',
  node_refused: 'узел сети отклонил транзакцию',
  failed_on_chain: 'транзакция попала в блок с ошибкой',
  expired: 'срок транзакции истёк, в блок она не попала',
  signature_check_failed: 'самопроверка подписи не прошла',
  outcome_unknown: 'узел не подтвердил исход',
  unknown: 'ошибка горячего кошелька',
}

const FAMILY_RU: Record<AutoWithdrawalsStatus['hotWallets'][number]['family'], string> = {
  evm: 'EVM',
  tron: 'Tron',
  solana: 'Solana',
}

function plural(count: number, one: string, few: string, many: string): string {
  const tail = count % 100
  if (tail >= 11 && tail <= 14) return many
  if (count % 10 === 1) return one
  if (count % 10 >= 2 && count % 10 <= 4) return few
  return many
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

/** A stuck row whose signed transaction can still land: failing it now could refund money that is about to leave. */
function stillValid(row: AdminWithdrawal): boolean {
  return row.status === 'broadcasting' && row.broadcastExpiresAt !== null && Date.parse(row.broadcastExpiresAt) > Date.now()
}

/* ------------------------------------------------------------------------ */
/* small parts of the queue                                                 */
/* ------------------------------------------------------------------------ */

/** The network code as a small mono chip (`tron`, `bsc`). */
function Net({ code }: { code: string }): JSX.Element {
  return <span className={styles.net}>{code}</span>
}

/** A hash with a copy button and, when the network has an explorer, an arrow that opens it. */
function TxRef({ hash, url, head = 6, tail = 4 }: { hash: string; url: string | null; head?: number; tail?: number }): JSX.Element {
  return (
    <span className={styles.tx}>
      <IdChip value={hash} head={head} tail={tail} label="хеш" bare className={styles.txChip} />
      {url ? (
        <Tooltip content="Открыть в обозревателе">
          <a href={url} target="_blank" rel="noreferrer" className={styles.explorer} aria-label="Открыть транзакцию в обозревателе">
            <ArrowUpRight aria-hidden="true" />
          </a>
        </Tooltip>
      ) : null}
    </span>
  )
}

/** One line under a row: an icon in the note's tone and the words. */
function Note({ tone = 'neutral', icon, title, children }: { tone?: 'neutral' | 'info' | 'down'; icon: ReactNode; title?: string; children: ReactNode }): JSX.Element {
  return (
    <div className={cx(styles.note, tone === 'down' ? styles.noteDown : tone === 'info' ? styles.noteInfo : '')} title={title}>
      <span className={styles.noteIcon} aria-hidden="true">
        {icon}
      </span>
      <span>{children}</span>
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/* automation summary                                                       */
/* ------------------------------------------------------------------------ */

/**
 * Automation at a glance: whether it is on and its worker alive, how much of
 * today's budget the system has spent, which chain families the hot wallets
 * can sign for, and what needs a person now. The rules themselves live in
 * «Настройки» (`withdrawals.auto.*`).
 */
function AutoPanel({ canEditRules, onOpenTab }: { canEditRules: boolean; onOpenTab: (tab: Tab) => void }): JSX.Element {
  const status = useQuery({ queryKey: ['withdrawals-auto'], queryFn: getAutoWithdrawalsStatus, refetchInterval: 15_000 })
  const s = status.data
  const attention = s ? s.attention.failedSends + s.attention.stuckBroadcasts : 0
  const used = s ? Number(s.usedTodayUsdt) : 0
  const cap = s ? Number(s.dailyCapUsdt) : 0
  const share = cap > 0 ? Math.min(1, used / cap) : 0

  const limits = s
    ? [
        'ниже порога двух операторов',
        s.perUserDailyCount > 0 ? `до ${s.perUserDailyCount} ${plural(s.perUserDailyCount, 'заявки', 'заявок', 'заявок')} на пользователя в сутки` : null,
        Number(s.perUserDailyUsdt) > 0 ? `до ${money(s.perUserDailyUsdt)} USDT на пользователя в сутки` : null,
      ].filter((part): part is string => part !== null)
    : []
  const conditions = s
    ? [
        'верификация одобрена',
        s.minAccountAgeHours > 0 ? `аккаунту от ${s.minAccountAgeHours} ч` : null,
        s.securityCooldownHours > 0 ? `${s.securityCooldownHours} ч после смены пароля или 2FA` : null,
        s.requireKnownAddress ? 'только на адреса, куда уже уходил вывод' : null,
      ].filter((part): part is string => part !== null)
    : []

  return (
    <section className={cx(styles.auto, s && !s.enabled ? styles.autoOff : '')} aria-labelledby="auto-title">
      <header className={styles.autoHead}>
        <span className={cx(styles.autoIcon, s?.enabled ? styles.autoIconOn : '')} aria-hidden="true">
          <Zap />
        </span>
        <div className={styles.autoHeadText}>
          <div className={styles.autoTitleRow}>
            <h2 id="auto-title" className={styles.autoTitle}>
              Автовывод
            </h2>
            {s ? (
              <Badge tone={s.enabled ? (s.worker.alive ? 'accent' : 'down') : 'neutral'} pulse={s.enabled && s.worker.alive}>
                {s.enabled ? 'включён' : 'выключен'}
              </Badge>
            ) : status.isPending ? (
              <Skeleton width={72} height={20} radius={999} />
            ) : null}
            {s?.enabled && s.enabledSince ? <span className={styles.autoSince}>для заявок с {when(s.enabledSince)}</span> : null}
          </div>
        </div>
        <div className={styles.autoActions}>
          {canEditRules ? (
            <Link to="/settings" className={styles.rulesLink}>
              <Settings2 aria-hidden="true" />
              Правила — в «Настройках»
            </Link>
          ) : (
            <span className={styles.rulesNote}>Правила задаёт администратор</span>
          )}
        </div>
      </header>

      {status.isError ? (
        <div className={styles.autoNotes}>
          <Callout tone="down">{describeError(status.error)}</Callout>
        </div>
      ) : (
        <>
          {(s?.enabled && !s.worker.alive) || (attention > 0 && s) ? (
            <div className={styles.autoNotes}>
              {s?.enabled && !s.worker.alive ? (
                <Callout tone="down" title={`Воркер автовывода не отвечает${s.worker.lastSeenAt ? ` с ${when(s.worker.lastSeenAt)}` : ''}`}>
                  Новые заявки ждут оператора. Он работает на экземпляре API с фоновыми задачами (COPY_ENGINE_ENABLED).
                </Callout>
              ) : null}
              {attention > 0 && s ? (
                <Callout
                  tone="warning"
                  title="Требуют внимания"
                  action={
                    <>
                      {s.attention.failedSends > 0 ? (
                        <Button size="sm" variant="secondary" onClick={() => onOpenTab('approved')}>
                          {s.attention.failedSends} {plural(s.attention.failedSends, 'сбой отправки', 'сбоя отправки', 'сбоев отправки')} — «К отправке»
                        </Button>
                      ) : null}
                      {s.attention.stuckBroadcasts > 0 ? (
                        <Button size="sm" variant="secondary" onClick={() => onOpenTab('broadcasting')}>
                          {s.attention.stuckBroadcasts} без подтверждённого исхода — «В отправке»
                        </Button>
                      ) : null}
                    </>
                  }
                />
              ) : null}
            </div>
          ) : null}

          <details className={styles.autoDetails}>
            <summary className={styles.autoSummary}>
              Лимиты и кошельки
              <ChevronDown aria-hidden="true" />
            </summary>
          <div className={styles.facts}>
            <div className={styles.fact}>
              <div className={styles.factHead}>
                <span className="overline">Бюджет на сутки UTC</span>
                {s ? <span className={styles.factShare}>{formatNumber(share * 100, share > 0 && share < 0.1 ? 1 : 0)} %</span> : null}
              </div>
              {s ? (
                <>
                  <div className={styles.figure}>
                    <CountUp value={s.usedTodayUsdt} decimals={2} className={styles.figureMain} />
                    <span className={styles.figureOf}>
                      из <span className="num">{formatNumber(cap, 2)}</span> USDT
                    </span>
                  </div>
                  <Progress value={used} max={cap > 0 ? cap : 1} tone={share >= 0.9 ? 'warning' : 'accent'} label="Израсходовано из дневного бюджета автовывода" className={styles.budgetBar} />
                  <dl className={styles.budgetFacts}>
                    <div>
                      <dt>осталось</dt>
                      <dd className="num">{money(s.remainingTodayUsdt)}</dd>
                    </div>
                    <div>
                      <dt>одобрено системой</dt>
                      <dd className="num">{s.approvedToday}</dd>
                    </div>
                  </dl>
                </>
              ) : (
                <FactSkeleton />
              )}
            </div>

            <div className={styles.fact}>
              <div className={styles.factHead}>
                <span className="overline">Одна заявка</span>
              </div>
              {s ? (
                <>
                  <div className={styles.figure}>
                    <span className={styles.figurePrefix}>до</span>
                    <span className={cx('num', styles.figureMain)}>{money(s.maxPerWithdrawalUsdt)}</span>
                    <span className={styles.figureOf}>USDT</span>
                  </div>
                  <ul className={styles.ruleList}>
                    {limits.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <FactSkeleton />
              )}
            </div>

            <div className={styles.fact}>
              <div className={styles.factHead}>
                <span className="overline">Условия</span>
              </div>
              {s ? (
                <ul className={styles.checkList}>
                  {conditions.map((line) => (
                    <li key={line}>
                      <Check aria-hidden="true" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <FactSkeleton />
              )}
            </div>

            <div className={styles.fact}>
              <div className={styles.factHead}>
                <span className="overline">Горячие кошельки</span>
              </div>
              {s ? (
                <>
                  <ul className={styles.wallets}>
                    {s.hotWallets.map((wallet) => (
                      <li key={wallet.family}>
                        <Tooltip
                          content={
                            wallet.sending
                              ? `Горячий кошелёк ${FAMILY_RU[wallet.family]} сейчас отправляет транзакцию. Отправка в той же сети подождёт её окончания (до 2 минут); другие сети семейства не ждут.${wallet.networks.length > 0 ? ` Подписывает для: ${wallet.networks.join(', ')}.` : ''}`
                              : wallet.canSign
                                ? wallet.networks.join(', ')
                                : 'Нет ключа в окружении или RPC у включённых сетей: отправка вручную с записью хеша'
                          }
                        >
                          <span className={styles.wallet} tabIndex={0}>
                            <span className={styles.walletName}>{FAMILY_RU[wallet.family]}</span>
                            <Badge size="sm" tone={wallet.sending ? 'warning' : wallet.canSign ? 'accent' : 'neutral'} pulse={wallet.sending}>
                              {wallet.sending ? 'отправляет…' : wallet.canSign ? `${wallet.networks.length} ${plural(wallet.networks.length, 'сеть', 'сети', 'сетей')}` : 'не подписывает'}
                            </Badge>
                          </span>
                        </Tooltip>
                      </li>
                    ))}
                  </ul>
                  <span className={styles.factHint}>
                    {s.hotWalletsSource === 'worker'
                      ? `по отчёту воркера, ${s.worker.lastSeenAt ? clock(s.worker.lastSeenAt) : '—'}`
                      : 'по этому экземпляру API: воркер не отчитывался'}
                  </span>
                </>
              ) : (
                <FactSkeleton />
              )}
            </div>
          </div>
          </details>
        </>
      )}
    </section>
  )
}

function FactSkeleton(): JSX.Element {
  return (
    <div className={styles.factSkeleton} aria-hidden="true">
      <Skeleton width="60%" height={20} />
      <Skeleton width="85%" height={10} />
      <Skeleton width="70%" height={10} />
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/* rows                                                                     */
/* ------------------------------------------------------------------------ */

/** The hot wallet's last transaction for the row: a candidate while in flight, the attempt after a failure. */
function Candidate({ row }: { row: AdminWithdrawal }): JSX.Element | null {
  if (row.broadcastTxHash === null || (row.status !== 'broadcasting' && row.status !== 'approved')) return null
  const label = row.status === 'broadcasting' ? 'кандидат' : 'попытка'
  return (
    <div className={styles.candidate}>
      <TxRef hash={row.broadcastTxHash} url={row.broadcastExplorerUrl} />
      <span
        className={styles.candidateLabel}
        title={row.broadcastNonce !== null ? 'Nonce горячего кошелька, на который подписана транзакция: если в обозревателе этот nonce уже потрачен другой транзакцией, кандидат в блок не попадёт' : undefined}
      >
        {label}
        {row.broadcastNonce !== null ? ` · nonce ${row.broadcastNonce}` : ''}
      </span>
    </div>
  )
}

/** The notes a row carries, if any: they get their own full-width line under the row. */
function hasNotes(row: AdminWithdrawal): boolean {
  return (
    row.rejectionReason !== null ||
    (row.status === 'pending' && ((row.auto?.decision === 'skipped' && row.auto.reasons.length > 0) || row.firstApprovedBy !== null)) ||
    (row.status === 'approved' && row.broadcastErrorCode !== null) ||
    (row.status === 'broadcasting' && row.broadcastStuck) ||
    (row.reversalFlag ?? null) !== null
  )
}

/** Under the row: why the automation left it, what the hot wallet said, whether a person must act. */
function RowNotes({ row, myId }: { row: AdminWithdrawal; myId: string | null }): JSX.Element {
  const reason = row.broadcastErrorCode ? BROADCAST_ERROR_RU[row.broadcastErrorCode] : null
  return (
    <div className={styles.notes}>
      {row.reversalFlag === 'pending' ? (
        <Note tone="down" icon={<CircleAlert />}>
          У пользователя открыт пересмотр зачисленного депозита — средства могут быть сторнированы. Сначала решите его в «Сторнированиях».
        </Note>
      ) : null}
      {row.reversalFlag === 'shortfall' ? (
        <Note tone="down" icon={<CircleAlert />}>
          По сторнированию депозита у пользователя осталась недостача — он должен платформе. Проверьте, прежде чем выпускать средства.
        </Note>
      ) : null}
      {row.status === 'pending' && row.firstApprovedBy !== null ? (
        <Note tone="info" icon={<UserCheck />}>
          Одобрено 1 из 2{row.firstApprovedAt ? ` · ${when(row.firstApprovedAt)}` : ''}
          {row.firstApprovedBy === myId ? ' — вами; второе одобрение даёт другой оператор' : ' — нужен второй оператор'}
        </Note>
      ) : null}
      {row.rejectionReason ? <Note icon={<MessageSquareText />}>{row.rejectionReason}</Note> : null}
      {row.status === 'pending' && row.auto?.decision === 'skipped' && row.auto.reasons.length > 0 ? (
        <Note icon={<Bot />}>Автовывод: {row.auto.reasons.map((code) => AUTO_REASON_RU[code] ?? code).join(' · ')}</Note>
      ) : null}
      {row.status === 'approved' && reason ? (
        <Note tone="down" icon={<CircleAlert />} title={row.broadcastError ?? undefined}>
          Сбой отправки: {reason}
        </Note>
      ) : null}
      {row.status === 'broadcasting' && row.broadcastStuck ? (
        <Note tone="down" icon={<TriangleAlert />} title={row.broadcastError ?? undefined}>
          {reason
            ? `Исход отправки неизвестен: ${reason}.`
            : row.broadcastTxHash
              ? `Нет ответа от горячего кошелька больше 15 минут: процесс отправки прервался после подписи.`
              : `Нет ответа от горячего кошелька больше 15 минут: отправка прервалась до подписи, в сеть ничего не ушло.`}
          {row.broadcastTxHash ? ' Найдите кандидата в обозревателе.' : ''}
          {row.broadcastTxHash && row.broadcastNonce !== null ? ` Если nonce ${row.broadcastNonce} горячего кошелька уже потрачен другой транзакцией, кандидат в блок не попадёт.` : ''}
          {stillValid(row) && row.broadcastExpiresAt ? ` Транзакция ещё может попасть в блок до ${clock(row.broadcastExpiresAt)}.` : ''}
        </Note>
      ) : null}
    </div>
  )
}

/** The request a dialog decides on: amount, route, person and the full destination. */
function DecisionSummary({ row }: { row: AdminWithdrawal }): JSX.Element {
  return (
    <div className={styles.summary}>
      <div className={styles.summaryTop}>
        <div className={styles.summaryAmount}>
          <span className="overline">Сумма</span>
          <span className={styles.summaryFigure}>
            <span className="num">{money(row.netAmount)}</span>
            <span className={styles.summaryUnit}>{row.assetSymbol}</span>
          </span>
          {Number(row.fee) > 0 || Number(row.platformFee) > 0 ? (
            <span className={styles.summaryHint}>
              {Number(row.fee) > 0 ? <>сеть <span className="num">{money(row.fee)}</span> {row.assetSymbol}{row.feeSource === 'dynamic' ? ' · газ' : ''}</> : null}
              {Number(row.fee) > 0 && Number(row.platformFee) > 0 ? ' · ' : null}
              {Number(row.platformFee) > 0 ? <>платформа <span className="num">{money(row.platformFee)}</span> {row.assetSymbol} ({money(String(Number(row.platformFeeRate) * 100), 0)}%)</> : null}
            </span>
          ) : null}
        </div>
        <div className={styles.summaryRoute}>
          <span className={styles.summaryAsset}>{row.assetSymbol}</span>
          <Net code={row.networkCode} />
        </div>
      </div>
      <dl className={styles.summaryList}>
        <div>
          <dt>Пользователь</dt>
          <dd className={styles.summaryPerson}>
            <Avatar name={row.userEmail} size={20} />
            <span className={styles.summaryEmail}>{row.userEmail}</span>
          </dd>
        </div>
        <div className={styles.summaryStack}>
          <dt>Адрес получателя</dt>
          <dd>
            <IdChip value={row.toAddress} full label="адрес" className={styles.summaryAddress} />
          </dd>
        </div>
      </dl>
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/* screen                                                                   */
/* ------------------------------------------------------------------------ */

/**
 * The queue behind the operator's main job. A withdrawal moves
 * pending → approved → sent; rejecting or failing returns the money. Every
 * decision asks for the second factor right before it fires. Requests that
 * clear the automation's rules are approved and sent by the system and carry
 * an «авто» mark; the rest say here why they wait for a person.
 */
export default function Withdrawals(): JSX.Element {
  const [tab, setTab] = useState<Tab>('pending')
  const [page, setPage] = useState(1)
  const [decision, setDecision] = useState<Decision | null>(null)
  const [reason, setReason] = useState('')
  const [txHash, setTxHash] = useState('')
  const queryClient = useQueryClient()
  const toast = useToast()
  const withTotp = useTotp()

  const status: WithdrawalStatus | undefined = tab === 'all' ? undefined : tab
  const list = useQuery({ queryKey: ['withdrawals', tab, page], queryFn: () => getWithdrawals(status, page), refetchInterval: 15_000 })
  const me = useQuery({ queryKey: ['me'], queryFn: getCurrentUser, staleTime: 60_000 })
  // The same query the block above runs (one cache entry): the send modal reads whether the row's wallet is busy.
  const auto = useQuery({ queryKey: ['withdrawals-auto'], queryFn: getAutoWithdrawalsStatus, refetchInterval: 15_000 })
  // The sidebar's query (one cache entry): the queue tabs show how many rows wait in them.
  const overview = useQuery({ queryKey: ['overview'], queryFn: getOverview, refetchInterval: 20_000, retry: false })
  const myId = me.data?.user.id ?? null
  const canEditRules = hasPermission(me.data?.user.role ?? '', 'settings')
  const tabs = TABS.map((option) =>
    option.value === 'pending' && overview.data
      ? { ...option, count: overview.data.pendingWithdrawals, tone: overview.data.pendingWithdrawals > 0 ? ('warning' as const) : undefined }
      : option.value === 'approved' && overview.data
        ? { ...option, count: overview.data.approvedWithdrawals, tone: overview.data.approvedWithdrawals > 0 ? ('warning' as const) : undefined }
        : option,
  )

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['withdrawals'] })
    void queryClient.invalidateQueries({ queryKey: ['withdrawals-auto'] })
    void queryClient.invalidateQueries({ queryKey: ['overview'] })
  }

  const act = useMutation({
    mutationFn: async ({ kind, row, code }: { kind: Decision['kind'] | 'approve'; row: AdminWithdrawal; code: string }) => {
      switch (kind) {
        case 'approve':
          return approveWithdrawal(row.id, code)
        case 'reject':
          return rejectWithdrawal(row.id, reason.trim(), code)
        case 'fail':
          return failWithdrawal(row.id, reason.trim(), code)
        case 'send':
        case 'record':
          return sendWithdrawal(row.id, txHash.trim() === '' ? undefined : txHash.trim(), code)
      }
    },
    onSuccess: (updated) => {
      toast(
        updated.status === 'pending' && updated.firstApprovedBy !== null
          ? `Заявка ${short(updated.id, 8, 0)}: первое одобрение записано, нужен второй оператор`
          : `Заявка ${short(updated.id, 8, 0)} ${REQUEST_RESULT_RU[updated.status] ?? statusLabel(updated.status)}`,
      )
      setDecision(null)
      setReason('')
      setTxHash('')
      refresh()
    },
    onError: (error) => {
      toast(describeError(error), true)
      // A failed broadcast changed the row (error on it, back to «К отправке»).
      refresh()
    },
  })

  const run = (kind: Decision['kind'] | 'approve', row: AdminWithdrawal): void => {
    void withTotp((code) => act.mutateAsync({ kind, row, code }))
  }

  const open = (next: Decision): void => {
    setReason('')
    // Never pre-filled: the hash to record is the one the operator found on the explorer.
    setTxHash('')
    setDecision(next)
  }

  const openTab = (next: Tab): void => {
    setTab(next)
    setPage(1)
  }

  const rows = list.data?.items ?? []
  const decisionTitle =
    decision?.kind === 'send' ? 'Отправить вывод' : decision?.kind === 'record' ? 'Записать отправку' : decision?.kind === 'reject' ? 'Отклонить заявку' : 'Отметить как неудавшийся'
  // Nothing can sign for the row's network (per the worker's report): the send is by hand, so the hash is required up front.
  const noSigner =
    decision?.kind === 'send' && auto.data !== undefined && !auto.data.hotWallets.some((wallet) => wallet.canSign && wallet.networks.includes(decision.row.networkCode))
  const needsHash = decision?.kind === 'record' || noSigner
  const needsReason = decision?.kind === 'reject' || decision?.kind === 'fail'
  const candidate = decision?.kind === 'record' ? (decision.row.broadcastTxHash ?? null) : null
  const hashMismatch = candidate !== null && txHash.trim().length >= 10 && txHash.trim().replace(/^0x/i, '').toLowerCase() !== candidate.replace(/^0x/i, '').toLowerCase()
  // The hot wallet that signs for the row's network is sending something else right now (on any replica).
  // The flag is per family; only a send on the same chain would make this one wait, and the note says so.
  const busyWallet = decision?.kind === 'send' ? (auto.data?.hotWallets.find((wallet) => wallet.sending && wallet.networks.includes(decision.row.networkCode)) ?? null) : null
  const look = decision ? DECISION_LOOK[decision.kind] : DECISION_LOOK.send
  const empty = EMPTY_RU[tab]

  return (
    <>
      <PageHeader title="Выводы" description="Проверка, одобрение и отправка заявок. Каждое решение — с кодом 2FA." />

      <div className="page-stack">
        <AutoPanel canEditRules={canEditRules} onOpenTab={openTab} />

        <Panel className={styles.queue}>
          <div className={styles.queueHead}>
            <Tabs value={tab} options={tabs} onChange={openTab} variant="underline" ariaLabel="Статус заявок" className={styles.queueTabs} />
            {list.data ? (
              <span className={styles.queueTotal}>
                <span className="num">{list.data.total}</span> {plural(list.data.total, 'заявка', 'заявки', 'заявок')}
              </span>
            ) : null}
          </div>

          {list.isError ? (
            <EmptyState compact tone="error" icon={<CircleAlert />} title="Список не загрузился" description={describeError(list.error)} />
          ) : rows.length === 0 && list.isPending ? (
            <TableSkeleton rows={5} headers={TABLE_HEADERS} />
          ) : rows.length === 0 ? (
            <EmptyState compact tone={empty.done ? 'success' : 'neutral'} icon={empty.done ? <CircleCheck /> : <Inbox />} title={empty.title} description={empty.description} />
          ) : (
            <Table ariaLabel="Заявки на вывод">
              <thead>
                <tr>
                  <th>{TABLE_HEADERS[0]}</th>
                  <th>{TABLE_HEADERS[1]}</th>
                  <th className={tableClass.right}>{TABLE_HEADERS[2]}</th>
                  <th>{TABLE_HEADERS[3]}</th>
                  <th>{TABLE_HEADERS[4]}</th>
                  <th>
                    <span className="sr-only">Действия</span>
                  </th>
                </tr>
              </thead>
              <tbody className={styles.rows}>
                {rows.map((row) => {
                  const notes = hasNotes(row)
                  const waitsForOther = row.status === 'pending' && row.firstApprovedBy !== null && row.firstApprovedBy === myId
                  return (
                    <Fragment key={row.id}>
                      <tr className={notes ? styles.withNotes : undefined}>
                        <td>
                          <div className={styles.person}>
                            <Avatar name={row.userEmail} size={28} className={styles.rowAvatar} />
                            <div className={styles.personText}>
                              <div className={styles.email} title={row.userEmail}>
                                {row.userEmail}
                              </div>
                              <div className={tableClass.sub}>{when(row.createdAt)}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className={styles.asset}>{row.assetSymbol}</div>
                          <div className={styles.route}>
                            <Net code={row.networkCode} />
                            {row.feeSource === 'dynamic' ? (
                              <span className={styles.gas} title={`Комиссия ${row.fee} ${row.assetSymbol} по цене газа`}>
                                газ
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className={`${tableClass.right} num`}>
                          <span className={styles.amount}>{money(row.netAmount, 2)}</span>
                          {Number(row.fee) > 0 ? <div className={cx(tableClass.sub, styles.fee)}>сеть {money(row.fee, 2)}</div> : null}
                          {Number(row.platformFee) > 0 ? <div className={cx(tableClass.sub, styles.fee)}>платформа {money(row.platformFee, 2)} ({money(String(Number(row.platformFeeRate) * 100), 0)}%)</div> : null}
                        </td>
                        <td>
                          <IdChip value={row.toAddress} head={6} tail={4} label="адрес" />
                          {row.txHash ? (
                            <div className={styles.hashLine}>
                              <TxRef hash={row.txHash} url={row.explorerUrl} />
                            </div>
                          ) : row.broadcastTxHash && (row.status === 'broadcasting' || row.status === 'approved') ? (
                            <div className={styles.hashLine}>
                              <Candidate row={row} />
                            </div>
                          ) : null}
                        </td>
                        <td>
                          <div className={styles.statusCell}>
                            <StatusPill status={row.status} />
                            {row.auto?.decision === 'approved' ? (
                              <Badge
                                size="sm"
                                tone="neutral"
                                icon={<Bot />}
                                title={
                                  row.status === 'failed'
                                    ? 'Одобрено системой по правилам автовывода; вывод не состоялся, доля бюджета вернулась'
                                    : `Одобрено системой по правилам автовывода · ${money(row.auto.valueUsdt)} USDT из дневного бюджета`
                                }
                              >
                                авто
                              </Badge>
                            ) : null}
                          </div>
                        </td>
                        <td className={tableClass.actions}>
                          {row.status === 'pending' ? (
                            <>
                              {waitsForOther ? (
                                <Badge tone="info" icon={<Users />} title="Ваше одобрение записано: второе даёт другой оператор">
                                  ждёт второго оператора
                                </Badge>
                              ) : (
                                <Button size="sm" variant="primary" onClick={() => run('approve', row)}>
                                  {row.requiresSecondApproval ? (row.firstApprovedBy === null ? 'Одобрить (1 из 2)' : 'Одобрить (2 из 2)') : 'Одобрить'}
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" className={styles.dangerGhost} onClick={() => open({ kind: 'reject', row })}>
                                Отклонить
                              </Button>
                            </>
                          ) : null}
                          {row.status === 'approved' ? (
                            <>
                              <Button size="sm" variant="primary" icon={<Send />} onClick={() => open({ kind: 'send', row })}>
                                Отправить
                              </Button>
                              <Button size="sm" variant="ghost" className={styles.dangerGhost} onClick={() => open({ kind: 'fail', row })}>
                                Не удалось
                              </Button>
                            </>
                          ) : null}
                          {row.status === 'broadcasting' && row.broadcastStuck ? (
                            <>
                              <Button size="sm" variant="primary" icon={<Hash />} onClick={() => open({ kind: 'record', row })}>
                                Записать хеш
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className={styles.dangerGhost}
                                onClick={() => open({ kind: 'fail', row })}
                                disabled={stillValid(row)}
                                title={stillValid(row) && row.broadcastExpiresAt ? `Транзакция ещё может попасть в блок до ${clock(row.broadcastExpiresAt)}` : undefined}
                              >
                                Не удалось
                              </Button>
                            </>
                          ) : null}
                          {row.status === 'broadcasting' && !row.broadcastStuck ? (
                            <Badge tone="info" pulse>
                              ждёт сеть…
                            </Badge>
                          ) : null}
                        </td>
                      </tr>
                      {notes ? (
                        <tr className={styles.notesRow}>
                          <td colSpan={6}>
                            <RowNotes row={row} myId={myId} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })}
              </tbody>
            </Table>
          )}
          {list.data ? <Pager page={page} pageSize={list.data.pageSize} total={list.data.total} onPage={setPage} /> : null}
        </Panel>
      </div>

      <Modal
        open={decision !== null}
        onClose={() => setDecision(null)}
        title={decisionTitle}
        icon={look.icon}
        tone={look.tone}
        subtitle={decision === null ? undefined : `Заявка ${short(decision.row.id, 8, 0)} · создана ${when(decision.row.createdAt)}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDecision(null)}>
              Отмена
            </Button>
            <Button
              variant={decision?.kind === 'send' || decision?.kind === 'record' ? 'primary' : 'danger-solid'}
              loading={act.isPending}
              disabled={(needsReason && reason.trim().length < 3) || (needsHash && txHash.trim().length < 10) || (decision?.kind === 'fail' && stillValid(decision.row))}
              onClick={() => decision && run(decision.kind, decision.row)}
            >
              {decision?.kind === 'send'
                ? txHash.trim() === '' && !noSigner
                  ? 'Отправить горячим кошельком'
                  : 'Записать ручную отправку'
                : decision?.kind === 'record'
                  ? 'Записать и списать резерв'
                  : decision?.kind === 'reject'
                    ? 'Отклонить и вернуть средства'
                    : 'Вернуть средства'}
            </Button>
          </>
        }
      >
        {decision ? <DecisionSummary row={decision.row} /> : null}
        {decision?.row.reversalFlag ? (
          <Callout tone="down" title={decision.row.reversalFlag === 'pending' ? 'Открыт пересмотр депозита' : 'Недостача по сторнированию'}>
            {decision.row.reversalFlag === 'pending'
              ? 'У пользователя открыт пересмотр зачисленного депозита: средства могут быть сторнированы. Решите его в «Сторнированиях» раньше, чем выпускать деньги.'
              : 'По сторнированию депозита у пользователя осталась недостача — он должен платформе.'}
          </Callout>
        ) : null}
        {decision?.kind === 'send' ? (
          <>
            {busyWallet ? (
              <Callout tone="warning">
                Горячий кошелёк {FAMILY_RU[busyWallet.family]} сейчас отправляет другую заявку. Если она в той же сети, эта начнётся после неё — ожидание до 2 минут.
              </Callout>
            ) : null}
            {noSigner ? (
              <Callout tone="info" title="Отправка вручную">
                Для сети {decision.row.networkCode} горячий кошелёк не подписывает: отправьте средства вручную с внешнего кошелька и вставьте хеш транзакции из
                обозревателя — заявка закроется как отправленная, резерв спишется один раз.
              </Callout>
            ) : null}
            <Field
              label="Хеш транзакции"
              hint={
                noSigner
                  ? 'Хеш из обозревателя сети заявки: не адрес и не ссылка.'
                  : 'Пусто — подпишет горячий кошелёк сети. Если он сейчас отправляет другую заявку в той же сети, отправка подождёт её — до 2 минут; Tron и Solana затем ждут подтверждения сети — до 3 минут. Не закрывайте окно и не нажимайте повторно. Если отправили вручную, вставьте хеш.'
              }
            >
              <Input mono leading={<Hash />} value={txHash} onChange={(event) => setTxHash(event.target.value)} placeholder="0x… / base58" />
            </Field>
          </>
        ) : decision?.kind === 'record' ? (
          <>
            {candidate ? (
              <Callout tone="info" title="Горячий кошелёк подписал транзакцию">
                <span className={styles.inlineTx}>
                  {decision.row.broadcastExplorerUrl ? (
                    <a href={decision.row.broadcastExplorerUrl} target="_blank" rel="noreferrer" className="num">
                      {short(candidate, 10, 8)}
                    </a>
                  ) : (
                    <span className="num">{short(candidate, 10, 8)}</span>
                  )}
                </span>
                . Откройте её в обозревателе: если она подтверждена, вставьте её хеш ниже — повторной подписи не будет, резерв спишется один раз. Если её нет
                {decision.row.broadcastExpiresAt ? ` и уже после ${clock(decision.row.broadcastExpiresAt)}` : ''}, закройте окно и отметьте неудачу.
              </Callout>
            ) : (
              <Callout tone="warning">
                Подписанной транзакции горячего кошелька у заявки нет: отправка прервалась до подписи. Записывайте хеш, только если отправили средства вручную.
              </Callout>
            )}
            <Field
              label="Хеш транзакции из обозревателя"
              error={hashMismatch ? 'Хеш не совпадает с подписанной транзакцией — проверьте, что это та же отправка.' : null}
            >
              <Input mono leading={<Hash />} value={txHash} onChange={(event) => setTxHash(event.target.value)} placeholder="0x… / base58" />
            </Field>
          </>
        ) : (
          <>
            {decision?.row.status === 'broadcasting' ? (
              <Callout tone={stillValid(decision.row) ? 'warning' : 'info'}>
                {decision.row.broadcastTxHash ? (
                  <>
                    Только если транзакции{' '}
                    {decision.row.broadcastExplorerUrl ? (
                      <a href={decision.row.broadcastExplorerUrl} target="_blank" rel="noreferrer" className="num">
                        {short(decision.row.broadcastTxHash, 10, 8)}
                      </a>
                    ) : (
                      <span className="num">{short(decision.row.broadcastTxHash, 10, 8)}</span>
                    )}{' '}
                    нет в обозревателе
                    {decision.row.broadcastExpiresAt
                      ? stillValid(decision.row)
                        ? `. Она ещё может попасть в блок до ${clock(decision.row.broadcastExpiresAt)} — до этого отметить неудачу нельзя.`
                        : ` и её срок истёк (${clock(decision.row.broadcastExpiresAt)}).`
                      : '. Транзакция EVM не истекает: убедитесь, что её нет и в очереди узла.'}
                  </>
                ) : (
                  'Подписанной транзакции у заявки нет: в сеть ничего не ушло.'
                )}{' '}
                Резерв вернётся на баланс пользователя.
              </Callout>
            ) : null}
            <Field label="Причина" hint="Пользователь увидит её в уведомлении. Не короче трёх символов.">
              <Textarea value={reason} onChange={(event) => setReason(event.target.value)} />
            </Field>
          </>
        )}
      </Modal>
    </>
  )
}
